import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Field } from 'redux-form';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { getBEMClasses } from 'apex-web/lib/helpers/cssClassesHelper';

import './TimeInForceListboxField.css';

const labelForValue = (options, value) => {
  const s = value != null && value !== '' ? String(value) : null;
  const o = options.find(x => String(x.value) === s);
  if (o) {
    return o.label;
  }
  return options[0] ? options[0].label : '';
};

export const InnerTimeInForceListbox = props => {
  const {
    input,
    meta,
    classModifiers,
    customClass,
    info,
    label,
    onSelect,
    options = [],
    disabled,
    showTriangles: _showTriangles
  } = props;
  const baseClasses = getBEMClasses(['ap-select', customClass]);
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const rootRef = useRef(null);
  const listboxId = useRef(
    `tif-listbox-${Math.random()
      .toString(36)
      .slice(2)}`
  ).current;

  const v = input && input.value;
  const display = labelForValue(options, v);

  const setMenuFromTrigger = () => {
    const el = rootRef.current;
    if (!el) {
      return;
    }
    const r = el.getBoundingClientRect();
    setMenuPos({
      top: r.bottom + 2,
      left: r.left,
      width: r.width
    });
  };

  useLayoutEffect(() => {
    if (open) {
      setMenuFromTrigger();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onScroll = () => setOpen(false);
    const onReposition = () => setMenuFromTrigger();
    const onKey = e => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    const onClickOut = e => {
      if (rootRef.current && rootRef.current.contains(e.target)) {
        return;
      }
      const menu = document.getElementById(listboxId);
      if (menu && menu.contains(e.target)) {
        return;
      }
      setOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onReposition);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClickOut);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onReposition);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClickOut);
    };
  }, [open, listboxId]);

  const onPick = val => {
    const next = String(val);
    input.onChange(next);
    if (onSelect) {
      onSelect(next);
    }
    setOpen(false);
  };

  const openMenu = () => {
    const el = rootRef.current;
    if (el) {
      const r = el.getBoundingClientRect();
      setMenuPos({
        top: r.bottom + 2,
        left: r.left,
        width: r.width
      });
    }
    setOpen(true);
  };

  if (!input) {
    return null;
  }

  const menuNode =
    open &&
    menuPos &&
    createPortal(
      <div
        id={listboxId}
        role="listbox"
        aria-label={label}
        className="time-in-force-listbox__menu-root"
        style={{
          position: 'fixed',
          zIndex: 10050,
          top: menuPos.top,
          left: menuPos.left,
          width: menuPos.width
        }}>
        {options.map((opt, idx) => {
          const selected = String(v) === String(opt.value);
          return (
            <button
              key={`${String(opt.value)}-${idx}`}
              type="button"
              role="option"
              className={classNames('time-in-force-listbox__option', {
                'time-in-force-listbox__option--selected': selected
              })}
              aria-selected={selected}
              disabled={opt.disabled}
              onMouseDown={e => {
                e.preventDefault();
                if (!opt.disabled) {
                  onPick(opt.value);
                }
              }}
              title={opt.title}
              tabIndex={-1}>
              {opt.label}
            </button>
          );
        })}
      </div>,
      document.body
    );

  return (
    <div
      className={classNames(
        'form-group',
        'time-in-force-listbox__field',
        baseClasses('select-wrapper', classModifiers)
      )}>
      <label
        htmlFor={`${input.name || 'timeInForce'}-control`}
        className={classNames('ap--label', baseClasses('select-label'))}>
        {label}
      </label>

      <div
        className={classNames('time-in-force-listbox', {
          'time-in-force-listbox--open': open
        })}
        ref={rootRef}
        data-test="time-in-force-listbox">
        <button
          type="button"
          className="form-control time-in-force-listbox__trigger"
          id={`${input.name || 'timeInForce'}-control`}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          onBlur={input.onBlur}
          onFocus={input.onFocus}
          onClick={() => {
            if (disabled) {
              return;
            }
            if (open) {
              setOpen(false);
            } else {
              openMenu();
            }
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (disabled) {
                return;
              }
              if (open) {
                setOpen(false);
              } else {
                openMenu();
              }
            }
          }}>
          <span className="time-in-force-listbox__trigger-label">
            {display}
          </span>
          <span className="time-in-force-listbox__chevron" aria-hidden />
        </button>
        {menuNode}
      </div>

      {info && (
        <small className={classNames('form-text-muted', baseClasses('select-info'))}>
          {info}
        </small>
      )}
      {meta && meta.dirty && meta.error && (
        <span className={classNames('time-in-force-listbox__err', baseClasses('select-error'))}>
          {meta.error}
        </span>
      )}
    </div>
  );
};

InnerTimeInForceListbox.defaultProps = {
  options: []
};

const TimeInForceListboxField = props => (
  <Field name={props.name} component={InnerTimeInForceListbox} {...props} />
);

TimeInForceListboxField.defaultProps = {
  name: 'timeInForce',
  options: []
};

TimeInForceListboxField.propTypes = {
  name: PropTypes.string
};

export default TimeInForceListboxField;
